<template lang="html">
  <div
    class="relative size-full bg-[#f5f5f5]"
    @pointerenter="onPointerEnter"
    @pointerleave="hovered = false"
  >
    <video
      ref="videoElement"
      class="size-full bg-[#f5f5f5] object-contain"
      :controls="autoplay || manualControls"
      muted
      playsinline
      preload="metadata"
      :poster="poster"
      :aria-label="label"
      @pointerdown.capture="onPointerDown"
      @click.capture="playback?.takeManualControl()"
      @keydown.capture="playback?.takeManualControl()"
    >
      <source :src="src" type="video/mp4">
    </video>
    <button
      v-if="!autoplay && !manualControls"
      type="button"
      class="absolute inset-0 size-full cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      :aria-label="`${label} — открыть плеер`"
      @click="openPlayer"
    />
  </div>
</template>

<script setup lang="ts">
import { useDocumentVisibility, useIntersectionObserver } from '@vueuse/core'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { createProductVideoPlayback } from '../../utils/productVideoPlayback'

const props = withDefaults(defineProps<{
  src: string
  poster?: string
  label: string
  active?: boolean
  autoplay?: boolean
}>(), {
  active: true,
  autoplay: false,
})

const videoElement = ref<HTMLVideoElement>()
const hovered = ref(false)
const manualControls = ref(false)
const visible = ref(false)
const documentVisibility = useDocumentVisibility()
let playback: ReturnType<typeof createProductVideoPlayback> | undefined

useIntersectionObserver(videoElement, ([entry]) => {
  visible.value = !!entry?.isIntersecting && entry.intersectionRatio > 0
}, { threshold: 0.01 })

function syncPlayback() {
  playback?.update({
    active: props.active,
    visible: visible.value,
    pageVisible: documentVisibility.value === 'visible',
    hovered: hovered.value,
  })
}

function onPointerEnter(event: PointerEvent) {
  if (event.pointerType !== 'touch') hovered.value = true
}

function onPointerDown(event: PointerEvent) {
  // Touch swipes must not take over playback; a completed tap does so on click.
  if (event.pointerType !== 'touch') playback?.takeManualControl()
}

function openPlayer() {
  manualControls.value = true
  void playback?.playManually()
}

watch([() => props.active, visible, documentVisibility, hovered], syncPlayback)

onMounted(() => {
  if (!videoElement.value) return
  playback = createProductVideoPlayback(videoElement.value, props.autoplay)
  syncPlayback()
})

onBeforeUnmount(() => playback?.dispose())
</script>
